import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, Loader2, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const AdminForgotPassword = () => {
  if (typeof document !== "undefined") document.title = "Reset your password — School Smiles Australia";
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const { forgotPassword } = useAuth();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setSent(true);
      toast.success("If that email is registered, a reset link has been sent.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F9FF] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-2xl px-5 py-3 shadow-sm border border-[#E5F0FA]">
            <Logo />
          </div>
        </div>
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(20,50,74,0.06)] border border-[#E5F0FA] p-8 md:p-10">
          <h1 className="font-display font-bold text-2xl md:text-3xl text-[#14324A]">Forgot your password?</h1>
          <p className="mt-2 text-sm text-[#4B6B86]">Enter your admin email and we'll send you a link to set a new one.</p>

          {sent ? (
            <div className="mt-6 text-center py-8" data-testid="admin-forgot-sent">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#F4F9FF] text-[#1677C8] inline-flex items-center justify-center">
                <MailCheck size={26} strokeWidth={2.5}/>
              </div>
              <h2 className="mt-4 font-display font-semibold text-lg text-[#14324A]">Check your inbox</h2>
              <p className="mt-2 text-sm text-[#4B6B86]">If an account exists for <span className="font-medium">{email}</span>, we've emailed a link that lets you set a new password. The link expires in 1 hour.</p>
              <Link to="/admin/login" className="btn-outline mt-6 inline-flex text-sm" data-testid="admin-forgot-back">
                <ArrowLeft size={16} strokeWidth={2.5}/> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="admin-forgot-form">
              <div>
                <Label className="text-sm font-medium text-[#14324A]" htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-forgot-email"
                  className="mt-2"
                />
              </div>
              {error && (
                <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="admin-forgot-submit">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                {submitting ? "Sending..." : "Send reset link"}
              </button>
              <Link to="/admin/login" className="block text-sm text-center text-[#4B6B86] hover:text-[#14324A]">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminForgotPassword;
